import React from 'react';
import { Briefcase, FileText, Shield, Users } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useSeason } from '../../context/SeasonContext';

export const AUDIENCE_STRIP_TEST_ID = 'audience-strip-section';

export const AUDIENCE_STRIP_KEYS = [
  'home.audience.badge',
  'home.audience.heading',
  'home.audience.subtitle',
  'home.audience.p1_title',
  'home.audience.p1_body',
  'home.audience.p2_title',
  'home.audience.p2_body',
  'home.audience.p3_title',
  'home.audience.p3_body',
  'home.audience.p4_title',
  'home.audience.p4_body',
];

export default function AudienceStrip() {
  const { t } = useLanguage();
  const { theme } = useSeason();

  const audiences = [
    {
      icon: Users,
      titleKey: 'home.audience.p1_title',
      titleFallback: 'Relocating families',
      bodyKey: 'home.audience.p1_body',
      bodyFallback:
        'Neighborhood fit, schools, commute, and daily life before you inquire.',
      accent: 'text-amber-600',
    },
    {
      icon: Briefcase,
      titleKey: 'home.audience.p2_title',
      titleFallback: 'Professional relocators',
      bodyKey: 'home.audience.p2_body',
      bodyFallback: 'Faster clarity on timing, cost truth, and practical corridors.',
      accent: 'text-signal-blue',
    },
    {
      icon: Shield,
      titleKey: 'home.audience.p3_title',
      titleFallback: 'PCS / TLA households',
      bodyKey: 'home.audience.p3_body',
      bodyFallback:
        'Base proximity, PCS timing, and honest cost clarity — practical support for your move.',
      accent: 'text-emerald-600',
    },
    {
      icon: FileText,
      titleKey: 'home.audience.p4_title',
      titleFallback: 'Contract & sponsored moves',
      bodyKey: 'home.audience.p4_body',
      bodyFallback: 'Reduce ambiguity before you commit time or signatures.',
      accent: 'text-purple-600',
    },
  ];

  return (
    <section
      className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-200"
      data-testid={AUDIENCE_STRIP_TEST_ID}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 border"
            style={{
              backgroundColor: `${theme.colors.primary}12`,
              borderColor: `${theme.colors.primary}35`,
            }}
          >
            <Users className="w-4 h-4" style={{ color: theme.colors.primary }} />
            <span style={{ color: theme.colors.primary }} className="text-sm font-medium">
              {t('home.audience.badge', 'Built for real relocation decisions')}
            </span>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-3">
            {t('home.audience.heading', 'WHO TLA HOUSING EUROPE HELPS')}
          </h2>

          <p className="text-slate-600 max-w-3xl mx-auto text-sm sm:text-base">
            {t(
              'home.audience.subtitle',
              'Families and professionals come first, with clear paths for military, contract, owner, and partner moves too.',
            )}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {audiences.map((audience) => (
            <div
              key={audience.titleKey}
              className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors"
            >
              <div
                className={`w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center mb-3 ${audience.accent}`}
              >
                <audience.icon className="w-5 h-5" aria-hidden />
              </div>
              <h3 className="font-heading text-base font-bold text-slate-800 mb-2">
                {t(audience.titleKey, audience.titleFallback)}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {t(audience.bodyKey, audience.bodyFallback)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
